import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Users, TreePine, Copy, LogIn } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import DigestCard from "@/components/app/DigestCard";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);

  // One-time greeting after a SocialPulse SSO sign-in.
  useEffect(() => {
    if (searchParams.get("sso") === "1") {
      toast.success("Welcome back via SocialPulse — your archive is open.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // Join
  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/v1/families");
      setFamilies(data.families || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/v1/families", { name: newName.trim(), description: newDesc.trim() || undefined });
      toast.success("Family archive created.");
      setNewName("");
      setNewDesc("");
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  }

  async function onJoin(e) {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/v1/families/join", { invite_code: inviteCode.trim() });
      toast.success(data.already_member ? "You're already in this family." : "Joined the family.");
      setInviteCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setJoining(false);
    }
  }

  function copyInvite(code) {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success("Invite code copied."))
      .catch(() => toast.error("Copy failed — please copy it manually."));
  }

  return (
    <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
      >
        <div>
          <div className="overline mb-3">Volume I · Your Archives</div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.9] tracking-tight">
            {user?.name?.split(" ")[0] || "Friend"},
            <br />
            <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
              welcome to the ledger.
            </span>
          </h1>
        </div>
        <button
          data-testid="dashboard-new-family-btn"
          onClick={() => setShowCreate((s) => !s)}
          className="btn-rust"
        >
          <Plus size={16} /> New family
        </button>
      </motion.div>

      {/* This month — anniversaries + hometown headlines */}
      <DigestCard />

      {/* Your life story — quick links */}
      <div className="grid md:grid-cols-2 gap-0 border border-[color:var(--ink-line)] mb-14" data-testid="dashboard-life-links">
        <Link to="/app/journal" data-testid="dashboard-journal-link" className="p-8 border-b md:border-b-0 md:border-r border-[color:var(--ink-line)] hover:bg-[color:var(--bone-2)] transition-colors block">
          <div className="overline mb-2">§ My Life</div>
          <h3 className="font-serif-display text-2xl mb-2">The book of you</h3>
          <p className="text-sm text-[color:var(--ink-soft)]">
            A private timeline of your life's chapters — dated, multilingual, yours alone.
          </p>
        </Link>
        <Link to="/app/interview" data-testid="dashboard-interview-link" className="p-8 hover:bg-[color:var(--bone-2)] transition-colors block">
          <div className="overline mb-2">§ Interview Me</div>
          <h3 className="font-serif-display text-2xl mb-2">Speak, and it is written</h3>
          <p className="text-sm text-[color:var(--ink-soft)]">
            Answer gentle questions aloud — your voice becomes written memory.
          </p>
        </Link>
      </div>

      {/* Create + Join row */}
      <div className="grid md:grid-cols-2 gap-0 border border-[color:var(--ink-line)] mb-14">
        {/* Create */}
        <div
          className={`p-8 border-b md:border-b-0 md:border-r border-[color:var(--ink-line)] ${
            showCreate ? "bg-[color:var(--bone-2)]" : ""
          }`}
        >
          <div className="overline mb-2">§ 01</div>
          <h3 className="font-serif-display text-2xl mb-3">Begin a new family</h3>
          {!showCreate ? (
            <p className="text-sm text-[color:var(--ink-soft)]">
              Every archive starts with a single name. Click "New family" to open the ledger.
            </p>
          ) : (
            <form onSubmit={onCreate} data-testid="dashboard-create-form" className="mt-4">
              <label className="block mb-4">
                <span className="overline">Family name</span>
                <input
                  data-testid="create-family-name"
                  required
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. The Kapoors of Peshawar"
                  className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)]"
                />
              </label>
              <label className="block mb-4">
                <span className="overline">A short chronicle</span>
                <textarea
                  data-testid="create-family-desc"
                  rows={2}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Optional — a sentence about your family."
                  className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)] resize-none"
                />
              </label>
              <div className="flex gap-3">
                <button
                  type="submit"
                  data-testid="create-family-submit"
                  disabled={creating}
                  className="btn-rust !py-2 !px-5 disabled:opacity-50"
                >
                  {creating ? "Opening…" : "Open archive"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="btn-ghost !py-2 !px-5"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Join */}
        <div className="p-8">
          <div className="overline mb-2">§ 02</div>
          <h3 className="font-serif-display text-2xl mb-3">Join by invitation</h3>
          <p className="text-sm text-[color:var(--ink-soft)] mb-4">
            Have an invite code from a relative? Enter it here to continue their record.
          </p>
          <form onSubmit={onJoin} data-testid="dashboard-join-form" className="flex gap-3">
            <input
              data-testid="join-invite-code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Invite code"
              className="flex-1 bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)]"
            />
            <button
              type="submit"
              data-testid="join-family-submit"
              disabled={joining}
              className="btn-ghost !py-2 !px-5 disabled:opacity-50"
            >
              <LogIn size={14} /> Join
            </button>
          </form>
        </div>
      </div>

      {/* Families list */}
      <div className="overline mb-6">Your families</div>
      {loading ? (
        <div className="text-sm text-[color:var(--ink-soft)]">Loading…</div>
      ) : families.length === 0 ? (
        <div
          data-testid="dashboard-empty-state"
          className="border border-dashed border-[color:var(--ink-line-strong)] p-12 text-center"
        >
          <div className="font-italic-serif italic text-2xl text-[color:var(--ink-soft)] mb-2">
            "The first page is always the hardest."
          </div>
          <p className="text-sm text-[color:var(--ink-soft)]">
            Open your first family archive above to begin.
          </p>
        </div>
      ) : (
        <div
          data-testid="dashboard-families-list"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[color:var(--ink-line)]"
        >
          {families.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="border-r border-b border-[color:var(--ink-line)] p-6 flex flex-col justify-between min-h-[220px] hover:bg-[color:var(--bone-2)] transition-colors"
              data-testid={`family-card-${f.id}`}
            >
              <div>
                <div className="overline mb-2 text-[color:var(--ink-soft)]">
                  Archive · {(f.members || []).length} member
                  {(f.members || []).length !== 1 ? "s" : ""}
                </div>
                <Link
                  to={`/app/family/${f.id}`}
                  data-testid={`family-open-${f.id}`}
                  className="font-serif-display text-2xl leading-tight link-underline"
                >
                  {f.name}
                </Link>
                {f.description && (
                  <p className="mt-2 text-sm text-[color:var(--ink-soft)] line-clamp-3">
                    {f.description}
                  </p>
                )}
              </div>
              <div className="mt-6 flex items-center justify-between text-xs">
                <button
                  data-testid={`family-copy-invite-${f.id}`}
                  onClick={() => copyInvite(f.invite_code)}
                  className="inline-flex items-center gap-2 text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
                  title="Copy invite code"
                >
                  <Copy size={12} /> {f.invite_code}
                </button>
                <Link
                  to={`/app/family/${f.id}`}
                  className="inline-flex items-center gap-2 link-underline"
                >
                  <TreePine size={14} /> Open tree
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
